import * as React from 'react';
import { Link, Body1, UIHeading3 } from '@cian/ui-kit';

import { FactRow } from '../../../AiSummaryModalShared/factsRows';
import { PeculiarityRow } from '../../../AiSummaryModalShared/peculiarityRows';
import type {
  IAiSummaryEmptyState,
  IAiSummaryNewbuilding,
  IAiSummaryResponse,
} from '../../../AiSummaryModalDesktop/types';
import { FooterBlock } from './parts/FooterBlock';
import { NoDataBlock } from './parts/NoDataBlock';
import * as styles from './styles.css';
import clsx from 'clsx';

export interface IMobileContentProps {
  summary: IAiSummaryNewbuilding;
  emptyState: IAiSummaryEmptyState;
  disclaimer: IAiSummaryResponse['disclaimer'];
  isRevealing?: boolean;
  onRevealComplete?(): void;
  revealKey?: number;
  call?: React.ReactNode;
  chat?: React.ReactNode;
  onShowPhone?(): void;
  onReportError?(newbuildingId: number): void;
  onCardClick?(newbuildingId: number): void;
}

export const MobileContent = ({
  summary,
  emptyState,
  disclaimer,
  isRevealing = false,
  onRevealComplete,
  revealKey = 0,
  call,
  chat,
  onShowPhone,
  onReportError,
  onCardClick,
}: IMobileContentProps): React.JSX.Element => {
  const hasHighlights = Boolean(summary.highlights?.items.length);
  const hasFeatures = Boolean(summary.features?.items.length);
  const highlightsItems = summary.highlights?.items ?? [];
  const featuresItems = summary.features?.items ?? [];

  const handleRevealEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onRevealComplete?.();
    }
  };

  return (
    <div className={styles['container']}>
      <div className={styles['building-container']}>
        <div className={styles['title-container']}>
          <Link
            color="default"
            fontSize="XL"
            fontWeight="bold"
            href={summary.fullUrl}
            onClick={() => onCardClick?.(summary.id)}
            target="_blank"
          >
            {summary.name}
          </Link>
          {summary.priceRange && <Body1>{summary.priceRange}</Body1>}
        </div>
        <div className={styles['call-container']}>
          {call}
          {chat}
        </div>
      </div>
      {!hasHighlights ? (
        <div
          className={clsx(styles['result-reveal-full-height'], isRevealing && styles['result-revealing'])}
          data-testid="mobile-content-reveal"
          key={revealKey}
          onAnimationEnd={handleRevealEnd}
        >
          <NoDataBlock {...emptyState} isFullContent newbuilding={summary} onShowPhone={onShowPhone} />
        </div>
      ) : (
        <>
          <div
            className={clsx(styles['facts-container'], isRevealing && styles['result-revealing'])}
            data-testid="mobile-content-reveal"
            key={`${summary.id}-${revealKey}`}
            onAnimationEnd={handleRevealEnd}
          >
            <UIHeading3>{summary.highlights?.title}</UIHeading3>
            <div className={styles['facts-row-wrapper']}>
              {highlightsItems.map(item => (
                <FactRow {...item} isMobile key={item.title} />
              ))}
            </div>
            {hasFeatures && (
              <>
                <UIHeading3>{summary.features?.title}</UIHeading3>
                <div className={styles['facts-row-wrapper']}>
                  {featuresItems.map(item => (
                    <PeculiarityRow {...item} isMobile key={item.title} />
                  ))}
                </div>
              </>
            )}
          </div>
          {!hasFeatures && (
            <NoDataBlock {...emptyState} isFullContent={false} newbuilding={summary} onShowPhone={onShowPhone} />
          )}
        </>
      )}
      {hasHighlights && hasFeatures && (
        <FooterBlock {...disclaimer} onReportError={() => onReportError?.(summary.id)} />
      )}
    </div>
  );
};
